export const validateDate = (dateStr: string | undefined): { isValid: boolean; error: string } => {
  if (!dateStr) {
    return { isValid: true, error: '' };
  }

  const s = dateStr.trim();
  if (s === '') {
    return { isValid: true, error: '' };
  }

  // Allow MM-DD-YYYY or YYYY-MM-DD formats (both common in the codebase)
  const mmddyyyy = /^\d{2}-\d{2}-\d{4}$/;
  const yyyymmdd = /^\d{4}-\d{2}-\d{2}$/;
  if (!(mmddyyyy.test(s) || yyyymmdd.test(s))) {
    return { isValid: false, error: 'Invalid date format. Use MM-DD-YYYY' };
  }

  return { isValid: true, error: '' };
};

export default validateDate;

import { ValidationError } from '../../../components/common/ErrorBanner';

export const parseFlexibleDate = (s: string) => {
  if (!s) return null;
  const mmddyyyy = /^(\d{2})-(\d{2})-(\d{4})$/;
  const yyyymmdd = /^(\d{4})-(\d{2})-(\d{2})$/;
  let m: RegExpExecArray | null = null;
  m = mmddyyyy.exec(s);
  if (m) {
    const mmNum = Number(m[1]);
    const dd = Number(m[2]);
    const yyyy = Number(m[3]);
    // validate month/day ranges
    if (mmNum < 1 || mmNum > 12) return null;
    if (dd < 1 || dd > 31) return null;
    const d = new Date(yyyy, mmNum - 1, dd);
    // ensure constructed date matches components (guards against overflow)
    if (d.getFullYear() !== yyyy || d.getMonth() !== mmNum - 1 || d.getDate() !== dd) return null;
    return d;
  }
  m = yyyymmdd.exec(s);
  if (m) {
    const yyyy = Number(m[1]);
    const mmNum = Number(m[2]);
    const dd = Number(m[3]);
    if (mmNum < 1 || mmNum > 12) return null;
    if (dd < 1 || dd > 31) return null;
    const d = new Date(yyyy, mmNum - 1, dd);
    if (d.getFullYear() !== yyyy || d.getMonth() !== mmNum - 1 || d.getDate() !== dd) return null;
    return d;
  }
  return null;
};

// helper utilities to simplify validation logic and reduce cognitive complexity
const isEmpty = (v: any) => !v || String(v).trim() === '';
const isDefaultOption = (opt: any) => !opt || String(opt?.value ?? opt?.id ?? '') === 'Select' || opt?.id === 0 || opt === null;
const addError = (arr: ValidationError[], field: string, message: string, ref?: any) => arr.push({ field, message, fieldRef: ref });

const checkCompletedDate = (
  sdStr: string | undefined,
  cdStr: string | undefined,
  ref: any,
  errors: ValidationError[],
) => {
  if (isEmpty(cdStr)) {
    addError(errors, 'Completed Date', 'Completed Date is required', ref);
    return;
  }
  const vd2 = validateDate(cdStr);
  if (!vd2.isValid) {
    addError(errors, 'Completed Date', vd2.error, ref);
    return;
  }
  const sd = parseFlexibleDate(sdStr ?? '');
  const cd = parseFlexibleDate(cdStr ?? '');
  if (sd && cd && cd.getTime() < sd.getTime()) {
    addError(errors, 'Completed Date', 'Completed Date must be later than Start Date', ref);
  }
};

export const validateProject = (values: {
  projectName?: string;
  valueCompass?: (string|number)[];
  focusArea?: any;
  startDate?: string;
  completedDate?: string;
  metricDescription?: string;
  dataSourceDescription?: string;
  requireMetricDescription?: boolean;
  requireDataSourceDescription?: boolean;
  smartGoal?: string;
  performanceMetric?: any;
  dataSource?: any;
  status?: any;
}, refs?: Record<string, any>): ValidationError[] => {
  const errors: ValidationError[] = [];
  const {
    projectName,
    valueCompass,
    focusArea,
    startDate,
    completedDate,
    metricDescription,
    dataSourceDescription,
    requireMetricDescription,
    requireDataSourceDescription,
    smartGoal,
    performanceMetric,
    dataSource,
    status,
  } = values;

  const add = (field: string, message: string, ref?: any) => addError(errors, field, message, ref);

  const checkRequiredProjectName = () => {
    if (isEmpty(projectName)) add('Name of project', 'Name of project is required', refs?.projectNameRef);
  };

  const checkValueCompass = () => {
    if (!valueCompass || valueCompass.length === 0) add('Value Compass', 'Select at least one Value Compass', refs?.valueCompassRef);
  };

  const checkFocusArea = () => {
    if (isDefaultOption(focusArea)) add('Focus Area', 'Select a Focus Area', refs?.focusAreaRef);
  };

  const checkStartDate = () => {
    if (isEmpty(startDate)) {
      add('Start Date', 'Start Date is required', refs?.startDateRef);
      return;
    }
    const vd = validateDate(startDate);
    if (!vd.isValid) add('Start Date', vd.error, refs?.startDateRef);
  };

  const checkCompletedIfClosed = () => {
    const isClosed = String(status?.value ?? status?.id ?? '').toLowerCase() === 'closed';
    if (isClosed) checkCompletedDate(startDate, completedDate, refs?.completedDateRef, errors);
  };

  const checkSmartGoal = () => {
    if (isEmpty(smartGoal)) add('Our SMART goal', 'Our SMART goal is required', refs?.smartGoalRef);
  };

  const checkMetricAndDataSource = () => {
    if (isDefaultOption(performanceMetric)) add('Performance metric', 'Select a Performance metric', refs?.performanceMetricRef);
    if (isDefaultOption(dataSource)) add('Data source for metric', 'Select a Data source for the metric', refs?.dataSourceRef);
  };

  const checkDescriptions = () => {
    if (requireMetricDescription && isEmpty(metricDescription)) add('Metric description', 'Metric description is required', refs?.metricDescriptionRef);
    if (requireDataSourceDescription && isEmpty(dataSourceDescription)) add('Data Source Description', 'Data Source Description is required', refs?.dataSourceDescriptionRef);
  };

  // run checks
  checkRequiredProjectName();
  checkValueCompass();
  checkFocusArea();
  checkStartDate();
  checkCompletedIfClosed();
  checkSmartGoal();
  checkMetricAndDataSource();
  checkDescriptions();

  return errors;
};
